import { PropsWithChildren } from "react";

import { useBalanceQuery } from "@bentolabs/kit/hooks";
import { cn } from "@bentolabs/kit/utils";
import { formatUnits } from "viem";

import { InputCurrency } from "features/input-currency";
import { Currency } from "shared/config";
import { Tab, Tabs } from "shared/ui/tabs";
import { mul } from "shared/utils";

import { useTrade } from "../context";

type Props = {
  currency: Currency | undefined;
  value: string | undefined;
  className?: string;
  onChange: (value: string) => void;
};

const getValue = (balance: bigint, decimal: number, absPercent: number) => {
  return formatUnits(BigInt(mul(balance, absPercent).toString()), decimal);
};

export const LabelWithTabs = ({
  className,
  children,
  value,
  currency,
  onChange,
}: PropsWithChildren<Props>) => {
  const { data: balanceData } = useBalanceQuery({ currency });

  const { theme } = useTrade();

  const handleClick = (absPercent: number) => () => {
    if (!balanceData || !currency) return;
    onChange(getValue(balanceData.balance, currency.decimals, absPercent));
  };

  const isActive = (absPercent: number) => {
    if (!balanceData || !value || !currency) return false;
    return (
      getValue(balanceData.balance, currency.decimals, absPercent) === value
    );
  };

  return (
    <InputCurrency.Label
      className={cn("flex w-full items-center justify-between", className)}
    >
      {children}
      <Tabs color={theme} size="26">
        <Tab
          disabled={!balanceData?.balance}
          isActive={isActive(0.25)}
          onClick={handleClick(0.25)}
        >
          25%
        </Tab>
        <Tab
          disabled={!balanceData?.balance}
          isActive={isActive(0.5)}
          onClick={handleClick(0.5)}
        >
          50%
        </Tab>
        <Tab
          disabled={!balanceData?.balance}
          isActive={isActive(0.75)}
          onClick={handleClick(0.75)}
        >
          75%
        </Tab>
        <Tab
          disabled={!balanceData?.balance}
          isActive={isActive(1)}
          onClick={handleClick(1)}
        >
          Max
        </Tab>
      </Tabs>
    </InputCurrency.Label>
  );
};
